import { isPlumtreeEnabled, Settings, UserRole, } from '@streamr/tv-engine';
import { areEqualBinaries, hexToBinary, toUserId, toUserIdRaw, } from '@streamr/utils';
import { Mutex } from 'async-mutex';
import { proxy, wrap } from 'comlink';
import { EventEmitter } from 'eventemitter3';
import Worker from 'web-worker';
import { canonicalUserId } from '../utils/canonicalUserId';
import { createWorker } from '../utils/workerUtils';
import { MediaManager } from './streamrapp/MediaManager';
import { StreamrWrapperProxy } from './streamrapp/StreamrWrapperProxy';
import { Timekeeper } from './streamrapp/Timekeeper';
function parseOneToOneStreamUrl(url) {
    const hashIndex = url.indexOf('#');
    if (hashIndex === -1) {
        throw new Error(`Invalid oneToOneStreamUrl: no # separator in "${url}"`);
    }
    const streamId = url.substring(0, hashIndex);
    const aesKeyHex = url.substring(hashIndex + 1);
    return { streamId, aesKey: hexToBinary(aesKeyHex) };
}
export class StreamrAppApi extends EventEmitter {
    streamrWrapper;
    networkingWorkerRaw;
    networkingWorkerProxy;
    mediaManager;
    mutex;
    heartbeatTimeoutRef;
    heartbeatIntervalRef;
    streamId;
    broadcasterUserId;
    aesKey;
    ownUserId;
    displayName;
    userManager;
    userRole;
    acceptedParticipantType = 0;
    oneToOneStreamsByUser = new Map();
    ownOneToOneStreamId;
    pendingN1GuestIds = new Set();
    n1StartInFlight = new Set();
    n1PendingReasons = new Map();
    constructor(opts) {
        super();
        this.streamId = opts.streamId;
        this.broadcasterUserId = opts.broadcasterUserId;
        this.aesKey = opts.aesKey;
        this.userRole = opts.initialUserRole ?? UserRole.VIEWER;
        this.mediaManager = new MediaManager();
        this.mutex = new Mutex();
    }
    async start(broadcasterPrivateKey) {
        await this.mutex.acquire();
        try {
            // Create the NetworkingWorker
            this.networkingWorkerRaw = this.createNetworkingWorkerInstance();
            this.networkingWorkerProxy = wrap(this.networkingWorkerRaw);
            // Set up the event callback for forwarding events from the worker
            await this.networkingWorkerProxy.setEventCallback(proxy(this.onNetworkingWorkerEvent));
            // Install the WebRTC bridge on the main thread. This sends a
            // bridge port to the NetworkingWorker via raw postMessage.
            // The worker intercepts this before Comlink and stores it for
            // forwarding to StreamrWrapper's internal NetworkNode worker.
            // biome-ignore lint/correctness/noUndeclaredDependencies: @streamr/dht is a file: dependency
            const { installWebrtcBridge } = await import('@streamr/dht');
            installWebrtcBridge(this.networkingWorkerRaw);
            const plumtree = isPlumtreeEnabled();
            if (plumtree) {
                console.log('StreamrAppApi: plumtree optimization enabled');
            }
            await this.networkingWorkerProxy.start(this.streamId, {
                aesKey: this.aesKey,
                diagnosticsEnabled: this.isGapDiagnosticsEnabled(),
                myPrivateKey: broadcasterPrivateKey,
                skipContentPartition: true,
                plumtree,
            });
            // Initialize MediaManager with the NetworkingWorker
            await this.mediaManager.init(this.networkingWorkerProxy);
            // Start workers for the initial role, then wire their ports
            // to the NetworkingWorker
            await this.startWorkersForRole(this.userRole);
            await this.mediaManager.wireNetworkingPorts({
                skipIndividualPorts: this.shouldSkipIndividualPorts(),
            });
            this.mediaManager.setPerTrackAudioEncodingEnabled(!this.shouldSkipIndividualPorts());
            this.mediaManager.setPerTrackVideoEncodingEnabled(!this.shouldSkipIndividualPorts());
            if (this.roleUsesSpeakerPartition(this.userRole)) {
                await this.activateSpeakerPartition();
            }
            this.syncSpeakerPartitionPublishing();
            this.ownUserId = toUserIdRaw((await this.networkingWorkerProxy.getMyUserId()));
            this.streamrWrapper = new StreamrWrapperProxy(this.networkingWorkerProxy, toUserId(this.ownUserId));
            this.emit('streamrWrapperReady', this.streamrWrapper);
            console.log('StreamrAppApi::start() ownUserId', toUserId(this.ownUserId), 'broadcasterUserId', toUserId(this.broadcasterUserId), 'userRole', this.userRole);
            this.setupHeartbeat();
        }
        finally {
            this.mutex.release();
        }
    }
    async stop() {
        await this.mutex.acquire();
        try {
            this.clearHeartbeatTimers();
            window.removeEventListener('beforeunload', this.onBeforeUnload);
            this.sendLeave();
            await this.deactivateContentPartition();
            await this.deactivateSpeakerPartition();
            await this.leaveAllOneToOneStreams();
            this.clearAllPendingN1();
            await this.mediaManager.stop();
            if (this.ownUserId &&
                areEqualBinaries(this.ownUserId, this.broadcasterUserId)) {
                await this.mediaManager.stopPublishingViewerStream();
            }
            await this.networkingWorkerProxy?.stop();
            this.networkingWorkerRaw?.terminate();
            this.networkingWorkerProxy = undefined;
            this.networkingWorkerRaw = undefined;
        }
        finally {
            this.mutex.release();
        }
    }
    getMediaManager() {
        return this.mediaManager;
    }
    isGapDiagnosticsEnabled() {
        try {
            return (window.localStorage.getItem('gapDiagnostics') === '1' ||
                new URL(window.location.href).searchParams.get('gapDiagnostics') === 'true');
        }
        catch {
            return false;
        }
    }
    getStreamrWrapper() {
        if (!this.streamrWrapper) {
            throw new Error('Streamr wrapper not initialized');
        }
        return this.streamrWrapper;
    }
    getNetworkingWorker() {
        if (!this.networkingWorkerProxy) {
            throw new Error('NetworkingWorker not initialized');
        }
        return this.networkingWorkerProxy;
    }
    async handleCanvasClick(x, y) {
        await this.mediaManager.handleCanvasClick(x, y);
    }
    clearHeartbeatTimers() {
        if (this.heartbeatTimeoutRef) {
            clearTimeout(this.heartbeatTimeoutRef);
            this.heartbeatTimeoutRef = undefined;
        }
        if (this.heartbeatIntervalRef) {
            clearInterval(this.heartbeatIntervalRef);
            this.heartbeatIntervalRef = undefined;
        }
    }
    // --- Partition helpers (single source of truth) ---
    async activateContentPartition() {
        if (!this.networkingWorkerProxy) {
            return;
        }
        await this.networkingWorkerProxy.joinPartition(0);
    }
    async deactivateContentPartition() {
        await this.networkingWorkerProxy?.leavePartition(0);
    }
    async activateSpeakerPartition() {
        if (!this.networkingWorkerProxy) {
            return;
        }
        await this.networkingWorkerProxy.joinPartition(2);
    }
    async deactivateSpeakerPartition() {
        await this.networkingWorkerProxy?.leavePartition(2);
    }
    syncSpeakerPartitionPublishing() {
        this.networkingWorkerProxy?.setSpeakerPartitionPublishingEnabled(this.roleUsesSpeakerPartition(this.userRole));
    }
    // --- Partition role helpers ---
    roleUsesContentPartition(role) {
        return role === UserRole.VIEWER || role === UserRole.GUEST;
    }
    roleUsesSpeakerPartition(role) {
        if (role === UserRole.SPEAKER)
            return true;
        if (role === UserRole.COHOST && this.acceptedParticipantType === 1) {
            return true;
        }
        return false;
    }
    shouldSkipIndividualPorts() {
        return (this.userRole === UserRole.COHOST &&
            this.acceptedParticipantType !== 1);
    }
    async startWorkersForRole(role) {
        const promises = [];
        if (this.roleNeedsPlayerWorker(role)) {
            promises.push(this.mediaManager.startVideoPlayerWorker());
        }
        if (this.roleNeedsDecoderWorker(role)) {
            promises.push(this.mediaManager.startVideoDecoderWorker());
        }
        if (this.roleNeedsEncoderWorkers(role)) {
            promises.push(this.mediaManager.startEncoderWorkers());
        }
        await Promise.all(promises);
    }
    roleNeedsPlayerWorker(role) {
        return role !== UserRole.BANNED;
    }
    roleNeedsDecoderWorker(role) {
        return role !== UserRole.BANNED && role !== UserRole.CAMERA;
    }
    roleNeedsEncoderWorkers(role) {
        return (role === UserRole.VIEWER ||
            role === UserRole.SPEAKER ||
            role === UserRole.COHOST ||
            role === UserRole.CAMERA ||
            role === UserRole.GUEST);
    }
    // --- Public role API ---
    async setAcceptedParticipantType(type) {
        const oldUseSpeaker = this.roleUsesSpeakerPartition(this.userRole);
        const oldSkipIndividual = this.shouldSkipIndividualPorts();
        this.acceptedParticipantType = type;
        const newUseSpeaker = this.roleUsesSpeakerPartition(this.userRole);
        const newSkipIndividual = this.shouldSkipIndividualPorts();
        if (oldUseSpeaker && !newUseSpeaker) {
            await this.deactivateSpeakerPartition();
        }
        else if (!oldUseSpeaker && newUseSpeaker) {
            await this.activateSpeakerPartition();
        }
        this.syncSpeakerPartitionPublishing();
        if (oldSkipIndividual && !newSkipIndividual) {
            await this.mediaManager.wireNetworkingPorts({
                skipIndividualPorts: false,
            });
        }
        if (oldSkipIndividual !== newSkipIndividual) {
            this.mediaManager.setPerTrackAudioEncodingEnabled(!newSkipIndividual);
            this.mediaManager.setPerTrackVideoEncodingEnabled(!newSkipIndividual);
        }
    }
    getUserRole() {
        return this.userRole;
    }
    getAcceptedParticipantType() {
        return this.acceptedParticipantType;
    }
    async setUserRole(newRole) {
        const oldRole = this.userRole;
        if (oldRole === newRole) {
            return;
        }
        console.log(`StreamrAppApi::setUserRole() ${oldRole} -> ${newRole}`);
        this.userRole = newRole;
        const oldContent = this.roleUsesContentPartition(oldRole);
        const newContent = this.roleUsesContentPartition(newRole);
        const oldSpeaker = this.roleUsesSpeakerPartition(oldRole);
        const newSpeaker = this.roleUsesSpeakerPartition(newRole);
        const oldNeedsEncoders = this.roleNeedsEncoderWorkers(oldRole);
        const newNeedsEncoders = this.roleNeedsEncoderWorkers(newRole);
        const oldNeedsDecoder = this.roleNeedsDecoderWorker(oldRole);
        const newNeedsDecoder = this.roleNeedsDecoderWorker(newRole);
        const oldNeedsPlayer = this.roleNeedsPlayerWorker(oldRole);
        const newNeedsPlayer = this.roleNeedsPlayerWorker(newRole);
        // Start workers that the new role needs (before joining partitions)
        if (!oldNeedsPlayer && newNeedsPlayer) {
            await this.mediaManager.startVideoPlayerWorker();
        }
        if (!oldNeedsDecoder && newNeedsDecoder) {
            await this.mediaManager.startVideoDecoderWorker();
        }
        if (!oldNeedsEncoders && newNeedsEncoders) {
            await this.mediaManager.startEncoderWorkers();
        }
        const anyWorkerStarted = (!oldNeedsPlayer && newNeedsPlayer) ||
            (!oldNeedsDecoder && newNeedsDecoder) ||
            (!oldNeedsEncoders && newNeedsEncoders);
        if (anyWorkerStarted) {
            await this.mediaManager.wireNetworkingPorts({
                skipIndividualPorts: this.shouldSkipIndividualPorts(),
            });
        }
        this.mediaManager.setPerTrackAudioEncodingEnabled(!this.shouldSkipIndividualPorts());
        this.mediaManager.setPerTrackVideoEncodingEnabled(!this.shouldSkipIndividualPorts());
        // Handle partition changes
        if (oldContent && !newContent) {
            await this.deactivateContentPartition();
        }
        else if (!oldContent && newContent) {
            await this.activateContentPartition();
        }
        if (oldSpeaker && !newSpeaker) {
            await this.deactivateSpeakerPartition();
        }
        else if (!oldSpeaker && newSpeaker) {
            await this.activateSpeakerPartition();
        }
        this.syncSpeakerPartitionPublishing();
        // Stop workers that the new role no longer needs (after leaving partitions)
        if (oldNeedsEncoders && !newNeedsEncoders) {
            await this.mediaManager.stopEncoderWorkers();
        }
        if (oldNeedsDecoder && !newNeedsDecoder) {
            await this.mediaManager.stopVideoDecoderWorker();
        }
        if (oldNeedsPlayer && !newNeedsPlayer) {
            await this.mediaManager.stopVideoPlayerWorker();
        }
        this.setHeartbeatInterval();
    }
    // --- One-to-one stream methods ---
    async joinOneToOneReceiver(userId, streamUrl) {
        const canonical = canonicalUserId(userId);
        const { streamId, aesKey } = parseOneToOneStreamUrl(streamUrl);
        this.oneToOneStreamsByUser.set(canonical, streamId);
        await this.networkingWorkerProxy?.joinOneToOneStream(streamId, aesKey);
        this.scheduleOneToOneRetry(canonical, streamId, aesKey);
        this.requestN1StartForGuest(canonical, 'one-to-one receiver joined');
    }
    scheduleOneToOneRetry(userId, streamId, aesKey, attempt = 0) {
        const MAX_RETRIES = 3;
        const RETRY_DELAY_MS = 5000;
        if (attempt >= MAX_RETRIES)
            return;
        setTimeout(async () => {
            if (!this.oneToOneStreamsByUser.has(userId))
                return;
            const publishers = this.mediaManager.getActivePublishers();
            const uid = canonicalUserId(userId);
            if (publishers.some((p) => canonicalUserId(p) === uid))
                return;
            console.log(`[StreamrAppApi] No media from ${userId.slice(0, 12)} after ${RETRY_DELAY_MS * (attempt + 1)}ms, re-joining one-to-one stream (attempt ${attempt + 1}/${MAX_RETRIES})`);
            try {
                await this.networkingWorkerProxy?.leaveOneToOneStream(streamId);
                await this.networkingWorkerProxy?.joinOneToOneStream(streamId, aesKey);
                this.scheduleOneToOneRetry(userId, streamId, aesKey, attempt + 1);
            }
            catch {
                // ignore retry errors
            }
        }, RETRY_DELAY_MS);
    }
    async joinOneToOnePublisher(streamUrl) {
        const { streamId, aesKey } = parseOneToOneStreamUrl(streamUrl);
        this.ownOneToOneStreamId = streamId;
        this.networkingWorkerProxy?.setOneToOneStreamId(streamId);
        await this.networkingWorkerProxy?.joinOneToOneStream(streamId, aesKey);
    }
    async leaveOneToOneStream(userId) {
        const canonical = canonicalUserId(userId);
        const streamId = this.oneToOneStreamsByUser.get(canonical);
        if (streamId) {
            this.oneToOneStreamsByUser.delete(canonical);
            this.clearPendingN1ForGuest(canonical);
            await this.networkingWorkerProxy?.leaveOneToOneStream(streamId);
        }
    }
    async leaveOwnOneToOneStream() {
        if (this.ownOneToOneStreamId) {
            const streamId = this.ownOneToOneStreamId;
            this.ownOneToOneStreamId = undefined;
            this.networkingWorkerProxy?.setOneToOneStreamId(undefined);
            await this.networkingWorkerProxy?.leaveOneToOneStream(streamId);
        }
    }
    // --- N-1 audio for guests ---
    async stopContentAudio() {
        await this.mediaManager.stopContentAudio();
    }
    async resumeContentAudio() {
        await this.mediaManager.resumeContentAudio();
    }
    waitForContentAudioReady(timeoutMs) {
        return new Promise((resolve) => {
            const onReady = () => {
                clearTimeout(timer);
                resolve(true);
            };
            const timer = setTimeout(() => {
                this.mediaManager.off('contentAudioReady', onReady);
                console.warn(`[StreamrAppApi] waitForContentAudioReady timed out after ${timeoutMs}ms`);
                resolve(false);
            }, timeoutMs);
            this.mediaManager.once('contentAudioReady', onReady);
        });
    }
    waitForOneToOneAudioReady(timeoutMs) {
        return new Promise((resolve) => {
            const onReady = () => {
                clearTimeout(timer);
                resolve(true);
            };
            const timer = setTimeout(() => {
                this.mediaManager.off('oneToOneAudioReady', onReady);
                console.warn(`[StreamrAppApi] waitForOneToOneAudioReady timed out after ${timeoutMs}ms`);
                resolve(false);
            }, timeoutMs);
            this.mediaManager.once('oneToOneAudioReady', onReady);
        });
    }
    requestN1StartForGuest(userId, reason) {
        const canonical = canonicalUserId(userId);
        this.pendingN1GuestIds.add(canonical);
        this.tryFulfillPendingN1ForGuest(canonical, reason).catch(console.error);
    }
    async startN1AudioForGuest(guestUserId) {
        const canonical = canonicalUserId(guestUserId);
        this.pendingN1GuestIds.add(canonical);
        await this.tryFulfillPendingN1ForGuest(canonical, 'explicit request');
    }
    async tryFulfillPendingN1ForGuest(guestUserId, reason) {
        const canonical = canonicalUserId(guestUserId);
        if (!this.pendingN1GuestIds.has(canonical))
            return;
        if (this.n1StartInFlight.has(canonical))
            return;
        const streamId = this.oneToOneStreamsByUser.get(canonical);
        if (!streamId) {
            this.logPendingN1Reason(canonical, 'waiting for one-to-one stream', reason);
            return;
        }
        if (!this.userManager) {
            this.logPendingN1Reason(canonical, 'waiting for user manager', reason);
            return;
        }
        const user = this.userManager.getUser(canonical);
        if (!user) {
            this.logPendingN1Reason(canonical, 'waiting for user', reason);
            return;
        }
        if (user.status !== UserRole.GUEST) {
            this.clearPendingN1ForGuest(canonical);
            return;
        }
        if (!this.mediaManager.getAudioTrackIdForUser(canonical)) {
            this.logPendingN1Reason(canonical, 'waiting for inbound audio track', reason);
            return;
        }
        this.n1StartInFlight.add(canonical);
        this.n1PendingReasons.delete(canonical);
        try {
            await this.mediaManager.startN1PublishingForGuest(canonical, streamId);
            this.pendingN1GuestIds.delete(canonical);
        }
        finally {
            this.n1StartInFlight.delete(canonical);
        }
    }
    logPendingN1Reason(guestUserId, pendingReason, triggerReason) {
        if (this.n1PendingReasons.get(guestUserId) === pendingReason)
            return;
        this.n1PendingReasons.set(guestUserId, pendingReason);
        console.log(`[StreamrAppApi] N-1 pending for ${guestUserId}: ${pendingReason} (${triggerReason})`);
    }
    clearPendingN1ForGuest(guestUserId) {
        const canonical = canonicalUserId(guestUserId);
        this.pendingN1GuestIds.delete(canonical);
        this.n1PendingReasons.delete(canonical);
        this.n1StartInFlight.delete(canonical);
    }
    clearAllPendingN1() {
        this.pendingN1GuestIds.clear();
        this.n1PendingReasons.clear();
        this.n1StartInFlight.clear();
    }
    clearPendingN1ForSession(userSessionId) {
        const userId = userSessionId.split(':')[0];
        if (userId) {
            this.clearPendingN1ForGuest(canonicalUserId(userId));
        }
    }
    async stopN1AudioForGuest(guestUserId) {
        this.clearPendingN1ForGuest(guestUserId);
        await this.mediaManager.stopN1PublishingForGuest(canonicalUserId(guestUserId));
    }
    async leaveAllOneToOneStreams() {
        const userIds = Array.from(this.oneToOneStreamsByUser.keys());
        for (const userId of userIds) {
            await this.leaveOneToOneStream(userId).catch(() => undefined);
        }
        await this.leaveOwnOneToOneStream().catch(() => undefined);
    }
    isBroadcaster() {
        return (this.ownUserId != null &&
            areEqualBinaries(this.ownUserId, this.broadcasterUserId));
    }
    publishBroadcastMetadata(broadcastMetadata) {
        this.networkingWorkerProxy?.publishBroadcastMetadata(broadcastMetadata);
    }
    async setContentPublishingEnabled(enabled) {
        this.networkingWorkerProxy?.setContentPublishingEnabled(enabled);
        if (enabled) {
            await this.activateContentPartition();
        }
        else {
            await this.deactivateContentPartition();
        }
    }
    // --- NetworkingWorker event handler ---
    onNetworkingWorkerEvent = (event) => {
        switch (event.type) {
            case 'receivedFrame':
                this.emit('receivedFrame');
                break;
            case 'broadcastMetadata':
                this.emit('broadcastMetadataChanged', event.broadcastMetadata);
                break;
            case 'pluginMessage':
                this.emit('pluginMessage', event.pluginMessage, event.publisherId);
                this.streamrWrapper?.emit('pluginMessage', event.pluginMessage, event.publisherId);
                break;
            case 'purchaseConfirmedMessage':
                if (!areEqualBinaries(event.publisherId, this.broadcasterUserId)) {
                    return;
                }
                this.emit('purchaseConfirmedMessage', event.purchaseConfirmedMessage, event.publisherId);
                break;
            case 'heartbeat':
                this.onHeartbeat(event.heartbeat, event.publisherId);
                break;
            case 'leave':
                this.clearPendingN1ForGuest(event.userId);
                this.mediaManager.handleNetworkLeave(event.userId, event.messageChainId);
                break;
            case 'permissionRequest':
                this.streamrWrapper?.emit('permissionRequest', event.permissionRequest, event.publisherId);
                break;
            case 'setUserRole':
                this.streamrWrapper?.emit('setUserRole', event.setUserRole, event.publisherId);
                break;
            case 'privateMessage':
                this.streamrWrapper?.emit('privateMessage', event.privateMessage, event.publisherId);
                break;
            case 'recorderInstruction':
                this.streamrWrapper?.emit('recorderInstruction', event.recorderInstruction, event.publisherId);
                break;
            case 'purchaseClaimedMessage':
                this.streamrWrapper?.emit('purchaseClaimedMessage', event.purchaseClaimedMessage, event.publisherId);
                break;
            case 'newVideoTrack':
                this.mediaManager.handleNewInboundVideoTrack(event.trackId, event.userSessionId, event.userId);
                break;
            case 'videoTrackDone':
                this.mediaManager.handleInboundVideoTrackDone(event.trackId);
                break;
            case 'newAudioTrack':
                if (event.isOneToOne) {
                    this.mediaManager.exemptTrackFromContentStop(event.trackId);
                }
                this.mediaManager.handleNewInboundAudioTrack(event.trackId, event.userSessionId, event.userId);
                this.requestN1StartForGuest(event.userId, 'inbound audio track discovered');
                break;
            case 'audioTrackDone':
                this.mediaManager.handleInboundAudioTrackDone(event.trackId);
                break;
            case 'staleSessionCleaned':
                this.mediaManager.handleStaleSessionCleaned(event.userSessionId, event.staleTracks);
                this.clearPendingN1ForSession(event.userSessionId);
                break;
            case 'timekeeperUpdate':
                Timekeeper.getInstance('producer').setLastTimestampMicros(event.lastTimestampMicros, 'NetworkingWorker');
                break;
            case 'remoteSpeechState':
                this.mediaManager.handleRemoteSpeechState(event.userSessionId, event.userId, event.audioLevel, event.isSpeaking);
                break;
        }
    };
    sendLeave = () => {
        this.networkingWorkerProxy?.publishLeave();
    };
    setDisplayName(name) {
        this.displayName = name;
    }
    setUserManager(userManager) {
        this.userManager = userManager;
        for (const guestUserId of this.pendingN1GuestIds) {
            this.tryFulfillPendingN1ForGuest(guestUserId, 'user manager available').catch(console.error);
        }
    }
    sendHeartbeat = () => {
        this.networkingWorkerProxy?.publishHeartbeat(false, this.displayName);
    };
    publishHeartbeatNow() {
        this.sendHeartbeat();
    }
    onBeforeUnload = () => {
        this.sendLeave();
    };
    getHeartbeatInterval() {
        const isElevated = this.userRole === UserRole.SPEAKER ||
            this.userRole === UserRole.COHOST ||
            this.userRole === UserRole.GUEST ||
            this.userRole === UserRole.CAMERA;
        return isElevated
            ? Settings.speakerHeartbeatInterval
            : Settings.defaultHeartbeatInterval;
    }
    setupHeartbeat() {
        window.addEventListener('beforeunload', this.onBeforeUnload);
        this.sendHeartbeat();
        const interval = this.getHeartbeatInterval();
        const randomDelay = Math.floor(Math.random() * interval);
        this.heartbeatTimeoutRef = setTimeout(() => {
            this.heartbeatTimeoutRef = undefined;
            this.sendHeartbeat();
            this.clearHeartbeatTimers();
            this.heartbeatIntervalRef = setInterval(() => {
                this.sendHeartbeat();
            }, interval);
        }, randomDelay);
    }
    setHeartbeatInterval() {
        this.clearHeartbeatTimers();
        const interval = this.getHeartbeatInterval();
        this.sendHeartbeat();
        this.heartbeatIntervalRef = setInterval(() => {
            this.sendHeartbeat();
        }, interval);
    }
    onHeartbeat = (heartbeat, publisherId) => {
        const userId = toUserId(publisherId);
        this.userManager?.handleHeartbeat(userId, heartbeat);
        if (heartbeat.displayName !== undefined &&
            heartbeat.displayName.length > 0) {
            this.userManager?.updateRemoteDisplayName(userId, heartbeat.displayName);
        }
    };
    // ========== Worker creation ==========
    createNetworkingWorkerInstance() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./streamrapp/NetworkingWorker.ts', import.meta.url), { type: 'module' });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./streamrapp/NetworkingWorker${ext}`, import.meta.url);
    }
}
